import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    DataGrid,
    GridColDef,
    GridActionsCellItem,
    GridRowId,
    GridRowParams,
    esES,
    GridToolbarContainer,
    GridValueGetterParams,
    GridRenderCellParams,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckoutPageHandler from './CheckoutPageHandler';
import { LoadingButton } from '@mui/lab';
import { getUrlByPublicId } from './Utils';

const CheckoutPageTable = (props: ICheckoutPageTableProps) => {
    const { data: rows, onChange, onBeforeNew } = props;
    const [open, setOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [rowIdToDelete, setRowIdToDelete] = useState<GridRowId | undefined>(undefined);
    const [rowIdSelected, setRowIdSelected] = useState<GridRowId | undefined>(undefined);

    const itemSelected = useMemo(() => {
        const item = rows.find(item => item.publicId === rowIdSelected);
        if (item !== undefined) return item;
        return onBeforeNew();
    }, [rowIdSelected, rows, onBeforeNew]);

    const editCheckoutPage = useCallback(
        (id: GridRowId) => () => {
            const item = rows.find(item => item.publicId === id);
            if (item === undefined) return;

            setOpen(true);
            setRowIdSelected(item.publicId);
        },
        [rows],
    );

    const deleteCheckoutPage = useCallback(
        (id: GridRowId) => () => {
            setConfirmDelete(true);
            setRowIdToDelete(id);
        },
        [rows],
    );

    const handleOnDelete = async () => {
        const item = rows.find(item => item.publicId === rowIdToDelete);
        
        if (rowIdToDelete === undefined) return;
        if (item === undefined) return;

        try {
            setIsDeleting(true);

            const _rows = rows.filter((row) => row.publicId !== rowIdToDelete);
            await onChange(_rows, {
                operation: 'DELETE',
                payload: item
            });
        } catch (error) {

        } finally {
            setIsDeleting(false);
            setConfirmDelete(false);
            setRowIdToDelete(undefined);
        }
    }


    const columns: GridColDef[] = useMemo(() => [
        {
            field: 'id',
            headerName: 'Id',
            width: 150,
            sortable: false,
            editable: false,
            valueGetter: (params: GridValueGetterParams<ICheckoutPage>) => {
                return params.row.publicId;
            },
        },
        {
            field: 'name',
            headerName: 'Curso',
            width: 200,
            sortable: false,
            editable: false,
            valueGetter: (params: GridValueGetterParams<ICheckoutPage>) => {
                return params.row.course.name;
            },
        },
        {
            field: 'link',
            headerName: 'Página',
            width: 300,
            sortable: false,
            editable: false,
            renderCell: (params: GridRenderCellParams<ICheckoutPage>) => (
                <Stack spacing={1} direction='row'>
                    <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ContentCopyIcon />}
                        onClick={() => {
                            navigator.clipboard.writeText(getUrlByPublicId(params.row.publicId));
                        }}
                    >
                        Copiar
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        href={getUrlByPublicId(params.row.publicId)}
                        target='_blank'
                    >
                        Ir a la página
                    </Button>
                </Stack>
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Opciones',
            width: 100,
            sortable: false,
            editable: false,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem icon={<EditIcon />} label="Editar" onClick={editCheckoutPage(params.id)} />,
                <GridActionsCellItem icon={<DeleteIcon />} label="Eliminar" onClick={deleteCheckoutPage(params.id)} />,
            ]
        },
    ], [editCheckoutPage]);

    return (
        <>
            <DataGrid
                getRowId={(row: ICheckoutPage) => row.publicId}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                autoHeight
                slots={{
                    toolbar: () => (
                        <GridToolbarContainer>
                            <Button
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setOpen(true);
                                    setRowIdSelected(undefined);
                                }}>
                                Agregar página
                            </Button>
                        </GridToolbarContainer>
                    )
                }}
                rowSelection={false}
                columns={columns}
                rows={rows}
            />
            <CheckoutPageHandler
                type={rowIdSelected !== undefined ? 'EDIT' : 'NEW'}
                open={open}
                payload={itemSelected}
                onSubmit={async (payload) => {
                    const _rows = [...rows];
                    const index = _rows.findIndex(item => item.publicId === payload.publicId);
                    if (index !== -1) _rows[index] = payload;
                    else _rows.push(payload);

                    await onChange(_rows, {
                        operation: rowIdSelected !== undefined ? 'EDIT' : 'NEW',
                        payload: payload
                    });
                }}
                onClose={() => {
                    setOpen(false);
                    setRowIdSelected(undefined);
                }}
            />
            <Dialog
                open={confirmDelete}
                onClose={() => {
                    if (isDeleting) return;
                    setConfirmDelete(false);
                }}
            >
                <DialogTitle>
                    ¿Deseas eliminar la página?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Nadie podrá acceder a la página después de esta acción
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={isDeleting}
                        onClick={() => setConfirmDelete(false)}
                        autoFocus>
                        Cancelar
                    </Button>
                    <LoadingButton
                        loading={isDeleting}
                        onClick={handleOnDelete}>
                        Eliminar
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );

};

export { CheckoutPageTable };